import {
    Caption,
    Grid,
    Image,
    useStyles,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import * as S from './ElementalPagePromo.style';
import type {
    ResponsiveImage,
    SidePromoShape,
} from '@modules/elemental-page/type';
import type { ReactNode } from 'react';
import { Link } from '@remix-run/react';
import { useCountryContext } from '@archipro-website/localisation';
import {
    YoutubeFill,
    PinterestFill,
    LinkedinBoxFill,
    InstagramLine,
    FacebookCircleFill,
} from '@archipro-design/icons';
import {
    SOCIAL_LINK_FACEBOOK,
    SOCIAL_LINK_INSTAGRAM,
    SOCIAL_LINK_LINKEDIN,
    SOCIAL_LINK_PINTEREST,
    SOCIAL_LINK_YOUTUBE,
} from '@modules/elemental-page/config/module-constants';
import { Icon } from '@modules/professional/component/overview-tab/main-info/SocialLinks';
import React from 'react';

interface ElementalPagePromoProps {
    promo: SidePromoShape;
    flagSelector: ReactNode;
    libraryItemId: number;
}

const ElementalPagePromo = ({
    promo,
    flagSelector,
    libraryItemId,
}: ElementalPagePromoProps) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const { code } = useCountryContext();
    if (mobile) {
        return null;
    }

    const generateImage = (
        firstPromoImage?: ResponsiveImage,
        promoUrl?: string,
    ) => {
        const promoImageSrc =
            mobile && firstPromoImage?.mobileSrc
                ? firstPromoImage.mobileSrc
                : firstPromoImage?.desktopSrc;

        if (!promoImageSrc) return null;

        const promoImage = (
            <div className={css(S.PromoImageContainer)}>
                <Image
                    src={promoImageSrc}
                    width={166}
                    height={177}
                    layout={'fill'}
                    objectFit={'cover'}
                    alt={''}
                />
            </div>
        );

        if (!promoUrl) return promoImage;
        return <Link to={promoUrl}>{promoImage}</Link>;
    };

    return (
        <div className={css(S.SidePromo)}>
            <div className={css(S.Head)}>
                <p>{code}</p>
                {flagSelector}
            </div>
            <div>
                <Caption variant={'03'} className={css(S.Title)}>
                    {promo.title}
                </Caption>
                <Caption variant={'04'} className={css(S.Subtitle)}>
                    {promo.content}
                </Caption>
                {generateImage(promo.firstPromoImage, promo.firstPromoUrl)}
                {generateImage(promo.secondPromoImage, promo.secondPromoUrl)}
            </div>
            <Grid columns={5} className={css(S.Footer)}>
                <Icon
                    Icon={YoutubeFill}
                    href={SOCIAL_LINK_YOUTUBE}
                    label="Youtube"
                    title={'about-us'}
                    itemID={libraryItemId}
                />
                <Icon
                    Icon={PinterestFill}
                    href={SOCIAL_LINK_PINTEREST}
                    label="Pinterest"
                    title={'about-us'}
                    itemID={libraryItemId}
                />
                <Icon
                    Icon={LinkedinBoxFill}
                    href={SOCIAL_LINK_LINKEDIN}
                    label="LinkedIn"
                    title={'about-us'}
                    itemID={libraryItemId}
                />
                <Icon
                    Icon={InstagramLine}
                    href={SOCIAL_LINK_INSTAGRAM}
                    label="Instagram"
                    title={'about-us'}
                    itemID={libraryItemId}
                />
                <Icon
                    Icon={FacebookCircleFill}
                    href={SOCIAL_LINK_FACEBOOK}
                    label="Facebook"
                    title={'about-us'}
                    itemID={libraryItemId}
                />
            </Grid>
        </div>
    );
};

export default ElementalPagePromo;
